// Phase 7 — Track B T-B2. FormField primitive.
//
// Wires a single input control into RHF + accessible labelling. Renders:
//
//   <label for=$id>{label}{required ? ' *' : ''}</label>
//   <child input id=$id aria-describedby=$descId aria-invalid />
//   <span id=$descId>{description}</span>     ← when description is provided
//   <FormError name={name} />                  ← when an error exists
//
// The child input MUST be a single React element (an Input, Textarea,
// Select, Checkbox, Switch, or Combobox from this module). FormField
// uses `React.cloneElement` to inject `id`, `aria-describedby`,
// `aria-invalid`, `name`, `value`, `onChange`, `onBlur`, and `ref`
// from RHF's `useController` — meaning consumers don't have to manually
// register each field.
//
// IDs are stable per-field (derived from the RHF name) so the label's
// `htmlFor` and the input's `id` always match, even across re-renders.

import * as React from 'react';
import { useController, useFormContext, type FieldValues, type Path } from 'react-hook-form';
import { cn } from '../../lib/cn.js';
import { FormError } from './FormError.js';

export interface FormFieldProps<TValues extends FieldValues = FieldValues>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** RHF field name (dotted path supported, e.g. `reminders.0.offset_days`). */
  name: Path<TValues>;
  /** Visible label text. */
  label: string;
  /** Optional helper text rendered below the input. */
  description?: string;
  /** When true, appends a visible asterisk + `aria-required="true"`. */
  required?: boolean;
  /** Single input element to wire (Input, Textarea, etc.). */
  children: React.ReactElement;
}

export function FormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  required,
  children,
  className,
  ...props
}: FormFieldProps<TValues>): JSX.Element {
  // useFormContext is null when this field is rendered outside <Form>.
  // We rely on useController throwing in that case so misuse fails loud
  // in tests rather than silently producing an unwired input.
  const ctx = useFormContext<TValues>();
  if (!ctx) {
    throw new Error(
      'FormField must be rendered inside a <Form> (which mounts <FormProvider>).',
    );
  }

  const { field, fieldState } = useController<TValues>({ name, control: ctx.control });

  // Stable ids derived from the field name so the label/input/description
  // wiring is consistent across renders.
  const safeName = String(name).replace(/[^A-Za-z0-9_-]/g, '-');
  const inputId = `field-${safeName}`;
  const descriptionId = description ? `${inputId}-desc` : undefined;
  const errorId = fieldState.error ? `${inputId}-err` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  // Inject RHF wiring into the child input. We forward field.ref so RHF
  // can call setFocus on validation errors (plan §2.10).
  const child = React.cloneElement(children, {
    id: inputId,
    name: field.name,
    value: field.value ?? '',
    onChange: field.onChange,
    onBlur: field.onBlur,
    ref: field.ref,
    'aria-invalid': fieldState.error ? true : undefined,
    'aria-describedby': describedBy,
    'aria-required': required ? true : undefined,
    // Preserve any explicit overrides the consumer passed.
    ...(children.props ?? {}),
  });

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...props}>
      <label
        htmlFor={inputId}
        className="text-body-strong text-text-primary"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-state-danger">
            *
          </span>
        ) : null}
      </label>
      {child}
      {description ? (
        <p id={descriptionId} className="text-caption text-text-muted">
          {description}
        </p>
      ) : null}
      {fieldState.error ? (
        <FormError id={errorId} name={String(name)} />
      ) : null}
    </div>
  );
}
FormField.displayName = 'FormField';
